import { useState, useEffect, useCallback } from 'react';
import { taskService } from '../../../services/quantifiedTaskService';
import { queueHomeRequest } from '../homeRequestQueue';

export interface PlannedStartThisWeekFilterInput {
  foremanAreaNames?: string[];
  serviceAreaName?: string;
}

export interface UsePlannedStartThisWeekCountResult {
  count: number;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetches count of quantified tasks whose planned start date falls within this week.
 */
export function usePlannedStartThisWeekCount(
  statuses: string[],
  plannedStartDateFrom: string,
  plannedStartDateTo: string,
  filter?: PlannedStartThisWeekFilterInput | null
): UsePlannedStartThisWeekCountResult {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const foremanAreaNames = filter?.foremanAreaNames;
  const serviceAreaName = filter?.serviceAreaName;
  const hasForemanFilter = !!(foremanAreaNames && foremanAreaNames.length > 0);
  const hasServiceFilter = !!serviceAreaName;

  const fetchCount = useCallback(async () => {
    if (statuses.length === 0 || !plannedStartDateFrom || !plannedStartDateTo) {
      setCount(0);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params: Parameters<typeof taskService.getQuantifiedTasksPaginated>[0] = {
        status: statuses,
        limit: 1,
        plannedStartDateFrom,
        plannedStartDateTo,
      };
      if (hasForemanFilter) {
        params.foremanArea = foremanAreaNames!;
      } else if (hasServiceFilter) {
        params.serviceArea = serviceAreaName!;
      }
      const response = await queueHomeRequest(() => taskService.getQuantifiedTasksPaginated(params));
      setCount(response.pagination?.totalCount ?? 0);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to fetch planned-start-this-week count')
      );
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    statuses.join(','),
    plannedStartDateFrom,
    plannedStartDateTo,
    hasForemanFilter ? foremanAreaNames!.join(',') : '',
    hasServiceFilter ? serviceAreaName : '',
  ]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return { count, loading, error, refetch: fetchCount };
}
